import { tz } from '@date-fns/tz';
import { format } from 'date-fns/format';
import { formatDistance } from 'date-fns/formatDistance';
import { enUS } from 'date-fns/locale/en-US';

const formatDistanceLocale = {
  lessThanXSeconds: '<{{count}}s',
  xSeconds: ' {{count}}s',
  halfAMinute: ' 30s',
  lessThanXMinutes: '<{{count}}min',
  xMinutes: ' {{count}}min',
  aboutXHours: '~{{count}}h',
  xHours: ' {{count}}h',
  xDays: ' {{count}}d',
  aboutXWeeks: '~{{count}}w',
  xWeeks: ' {{count}}w',
  aboutXMonths: '~{{count}}mo',
  xMonths: ' {{count}}mo',
  aboutXYears: '~{{count}}y',
  xYears: ' {{count}}y',
  overXYears: '>{{count}}y',
  almostXYears: '~{{count}}y',
};

const formatDistanceShort = (token: keyof typeof formatDistanceLocale, count: number) => {
  const result = (formatDistanceLocale[token] as string).replace('{{count}}', `${count}`);

  return result;
};

export const formatDate = (date: Date, today: Date) => {
  return formatDistance(date, today, {
    locale: {
      ...enUS,
      formatDistance: formatDistanceShort,
    },
  });
};

/** Format a Date to 'yyyy-MM-dd' (ISO date string for API queries and URL params). */
export const formatDateISO = (date: Date): string => format(date, 'yyyy-MM-dd');

/** Format a Date in a specific IANA timezone (e.g., 'America/Chicago'). */
export const formatInStoreTimezone = (date: Date, timezone: string, formatStr: string): string =>
  format(date, formatStr, { in: tz(timezone) });

// Locale pinned to en-US for a stable format across browsers.
const SHORT_DATE = { month: 'short', day: 'numeric', year: 'numeric' } as const;

/** Format a Date as 'Aug 26, 2026' — for dates shown in prose to store owners. */
export const formatShortDate = (date: Date): string => date.toLocaleString('en-US', SHORT_DATE);

/** Format a Date as 'Aug 26, 2026, 3:04 PM'. Agrees with formatShortDate by construction. */
export const formatAdminTimestamp = (date: Date): string =>
  date.toLocaleString('en-US', { ...SHORT_DATE, hour: 'numeric', minute: '2-digit' });
